import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import Maybe from "components/__new/common/Maybe";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import Print from "components/__new/common/Print";
import Table from "components/__new/common/Table";
import { TableContentCustom } from "components/__new/common/TableContent";
import { renderStatusColor } from "utils/statusColor";
import * as employeesAction from "store/entities/employees/action";
import BulkUpload from "components/__new/employees/BulkUpload";


const UploadResult = () => {
    const { jobId } = useParams();
    const dispatch = useDispatch();
    const { employeeUploadStatusLoading, employeeUploadStatus } = useSelector((s) => s.entities.employees);

    let interval = useRef();
    const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();

    const [isOpenBulkUpload, setIsOpenBulkUpload] = useState(false);
    const [status, setStatus] = useState("");

    const [uploadResultSuccess, setUploadResultSuccess] = useState([]);
    const [uploadResultSuccessLength, setUploadResultSuccessLength] = useState(0);
    const [uploadResultErrors, setUploadResultErrors] = useState([]);
    const [uploadResultErrorsLength, setUploadResultErrorsLength] = useState(0);
    const [employeeUploadStatusDownload, setEmployeeUploadStatusDownload] = useState([]);
    const [employeeUploadStatusDownloadLoading, setEmployeeUploadStatusDownloadLoading] = useState(false);


    const renderResults = (type, data) => {
        let response = [];
        let responseEmailAndPhoneNumber = [];
        data?.map((d) => {
            if (!responseEmailAndPhoneNumber.includes(d.email) && !responseEmailAndPhoneNumber.includes(d.phoneNumber)){
                const entry = {...d, status: (type === "success") ? "Uploaded" : "Failed"};
                response.push(entry);
                responseEmailAndPhoneNumber.push(d.email);
            }
            return null;
        })
        return response;
    }


    const getResults = async () => {
        dispatch(employeesAction.uploadEmployeesExcelStatus(jobId));
    }


    const getDownloadList = () => {
        let data = [];
        if (employeeUploadStatus?.data?.successList){
            data = [...data, ...employeeUploadStatus?.data?.successList];
        }
        if (employeeUploadStatus?.data?.errorList){
            data = [...data, ...employeeUploadStatus?.data?.errorList];
        }
        setEmployeeUploadStatusDownload(data);
    }


    useEffect(() => {
        if (jobId){
            getResults();
            
            interval.current = setInterval(() => {
                getResults();
            },  15 * 1000); 
        }
        // eslint-disable-next-line
    }, [jobId])


    useEffect(() => {
        setIsOpenBulkUpload(false);
        // eslint-disable-next-line
    }, [employeeUploadStatus])

    
    useEffect(() => {
        if (employeeUploadStatus?.status === "success"){
            const data = employeeUploadStatus?.data;

            const newUploadResultSuccess = renderResults("success", data.successList);
            const newUploadResultSuccessLength = newUploadResultSuccess.length;
            const newUploadResultErrors = renderResults("error", data.errorList);
            const newUploadResultErrorsLength = newUploadResultErrors.length;
            
            setUploadResultSuccess(newUploadResultSuccess);
            setUploadResultSuccessLength(newUploadResultSuccessLength);
            setUploadResultErrors(newUploadResultErrors);
            setUploadResultErrorsLength(newUploadResultErrorsLength);
            
            if (newUploadResultSuccessLength + newUploadResultErrorsLength === employeeUploadStatus?.data?.totalCount){
                setStatus("cancelInterval");
            }
            else{
                setStatus("ongoing");
            }
        }
        // eslint-disable-next-line
    }, [employeeUploadStatus])


    useEffect(() => {
        if (status === "cancelInterval"){
            clearInterval(interval.current);
        }
        // eslint-disable-next-line
    }, [status])


    return (
        <div>
                
                
            <DynamicHeadTag
                headerText="Bulk upload result | Employees"
                headerDescription="Manage and monitor your employees' payment activity."
            />


            <BulkUpload
                isOpen={isOpenBulkUpload}
                setIsOpen={setIsOpenBulkUpload}
            />


            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title flex">
                        Uploaded Employees
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3">
                    <Link to="/people/employees" className="btn btn-md btn-transparent-primary">
                        All Employees
                    </Link>
                    <div onClick={() => setIsOpenBulkUpload(true)} className="btn btn-md btn-ep-primary">
                        New Bulk Upload
                    </div>
                </div>
            </div>


            <div className="mt-6 box">
                <div className="flex flex-wrap justify-between">
                    <div className="max-w-sm font-bold flex flex-wrap flex-grow items-center sm:mr-6">
                        <Maybe condition={uploadResultSuccess && uploadResultErrors}>
                            <span className="text-md mr-3">
                                {uploadResultSuccessLength + uploadResultErrorsLength} of {employeeUploadStatus?.data?.totalCount} completed
                            </span>
                            <Maybe condition={uploadResultSuccessLength + uploadResultErrorsLength < employeeUploadStatus?.data?.totalCount}>
                                <span className="label label-yellow" style={{"padding": "7px 16px"}}>
                                    <FontAwesomeIcon icon="circle-notch" spin className="mr-2" />
                                    Uploading employees...
                                </span>
                            </Maybe>
                        </Maybe>
                    </div>

                    <Maybe condition={uploadResultSuccess && uploadResultErrors}>
                        <div className="flex flex-wrap space-x-3 sm:space-x-6">
                            <div>
                                <DownloadAsCSV
                                    ref={downloadAsCSVRef}
                                    tooltip="Download upload result list"
                                    filename="Employees upload result.csv"
                                    columns={[
                                        { label: "First Name", key: "firstName" },
                                        { label: "Last Name", key: "lastName" },
                                        { label: "Email Address", key: "email" },
                                        { label: "Phone Number", key: "phoneNumber" },
                                        { label: "Status", key: "status" },
                                        { label: "Reason", key: "message" },
                                    ]}
                                    click={() => {
                                        setEmployeeUploadStatusDownloadLoading(true);
                                        getDownloadList();
                                        setTimeout(() => {
                                            setEmployeeUploadStatusDownloadLoading(false);
                                            downloadAsCSVRef.current?.downloadData();
                                        }, [2000])
                                    }}
                                    loading={employeeUploadStatusDownloadLoading}
                                    response={employeeUploadStatusDownload}
                                />
                            </div>

                            <div>
                                <Print component={printComponentRef} />
                            </div>
                        </div>
                    </Maybe>
                </div>

                <div className="mt-6">
                    <div ref={printComponentRef}>
                        
                        <div className="mb-4 text-xl hidden print:block">
                            Uploaded Employees
                        </div>

                        <Table>
                            <Table.Head>
                                <th>SN</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email Address</th>
                                <th>Phone Number</th>
                                <th>Status</th>
                                <th>Reason</th>
                            </Table.Head>
                            <Table.Body>

                                <Maybe condition={employeeUploadStatusLoading === true}>
                                    <TableContentCustom
                                        loading={true}
                                        text="Checking status"
                                        colspan={7}
                                    />
                                </Maybe>

                                <Maybe condition={employeeUploadStatusLoading === false && employeeUploadStatus && employeeUploadStatus?.totalCount === 0}>
                                    <TableContentCustom
                                        icon="unlink"
                                        text="No status found"
                                        colspan={7}
                                    />
                                </Maybe>

                                <Maybe condition={employeeUploadStatusLoading === false && uploadResultSuccessLength === 0 && uploadResultErrorsLength === 0}>
                                    <TableContentCustom
                                        loading={true}
                                        text="Status: uploading employees"
                                        colspan={7}
                                    />
                                </Maybe>

                                {employeeUploadStatusLoading === false && uploadResultErrors && uploadResultErrorsLength > 0 && 
                                    uploadResultErrors.map((employee, index) =>
                                        <tr key={index}>
                                            <td>
                                                {++index}
                                            </td>
                                            <td>
                                                {employee.firstName}
                                            </td>
                                            <td>
                                                {employee.lastName}
                                            </td>
                                            <td>
                                                {employee.email}
                                            </td>
                                            <td>
                                                {employee.phoneNumber}
                                            </td>
                                            <td>
                                                <div className={"label " + renderStatusColor("failed")}>
                                                    Failed
                                                </div>
                                            </td>
                                            <td style={{"minWidth":"300px","whiteSpace":"normal"}}>
                                                <div className="break-words">
                                                    {employee.message}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                }
                                
                                {employeeUploadStatusLoading === false && uploadResultSuccess && uploadResultSuccessLength > 0 && 
                                    uploadResultSuccess.map((employee, index) =>
                                        <tr key={index}>
                                            <td>
                                                {uploadResultErrorsLength + index + 1}
                                            </td>
                                            <td>
                                                {employee.firstName}
                                            </td>
                                            <td>
                                                {employee.lastName}
                                            </td>
                                            <td>
                                                {employee.email}
                                            </td>
                                            <td>
                                                {employee.phoneNumber}
                                            </td>
                                            <td>
                                                <div className={"label " + renderStatusColor("success")}>
                                                    Uploaded
                                                </div>
                                            </td>
                                            <td style={{"minWidth":"300px","whiteSpace":"normal"}}>
                                                <div className="break-words">
                                                    {employee.message}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                }

                            </Table.Body>
                        </Table>

                    </div>
                </div>
            </div>

        </div>
    )
}


export default UploadResult;